// Mirrors of the tristar-corp-api models the site uses. Dates arrive as ISO strings.

export enum AccountState {
    Disabled = 0,
    Enabled = 1
}

// Claim values are the API's (Src/Models/ClaimTypes.ts)
export const SiteAdministratorClaim = 'https://tristaradvanced.com/claims/SiteAdministrator';
export const UserClaim = 'https://tristaradvanced.com/claims/User';
// A customer, listed under Customers rather than Users. Never also a SiteAdministrator (the API refuses it).
export const ExternalUserClaim = 'https://tristaradvanced.com/claims/ExternalUser';
// In the Early Access Program (the EAP badge): can be given Early Access licenses.
export const EarlyAccessUserClaim = 'https://tristaradvanced.com/claims/EarlyAccessUser';

// The refresh token isn't in the body: it's an HttpOnly cookie on the API's domain that scripts never see.
export interface TokenSet {
    accessToken: string;
    avatarUrl: string;
}

export type MfaMethod = 'totp' | 'email' | 'recovery';

// The password grant's answer when the account has MFA on: the tokens come once a code is verified.
export interface MfaChallenge {
    mfaRequired: true;
    mfaToken: string;
    methods: MfaMethod[];
    // Emailed codes are one of the methods, but mail to the account's address bounces.
    emailUndeliverable?: boolean;
}

export interface MfaStatus {
    totpEnabled: boolean;
    emailEnabled: boolean;
    recoveryCodesRemaining: number;
}

export interface TotpSetup {
    secret: string;
    otpauthUri: string;
}

export interface UserViewModel {
    username: string;
    emailAddress: string;
    displayName: string;
    status: AccountState;
    claims: string[];
    // The avatar on the CDN, or empty. Set through ProfileApi.setAvatar, never directly.
    avatarUrl: string;
    timezone: string;
    language: string;
    defaultProjectId?: string;
    createdOn: string;
    createdBy: string;
    modifiedOn: string;
    modifiedBy: string;
    mfa: MfaStatus;
    // Only a confirmed address can be used to sign in.
    emailConfirmed: boolean;
    // SES reported a permanent bounce for emailAddress: mail to it doesn't arrive. Cleared when the user confirms an
    // address, or by an administrator (UsersApi.clearEmailUndeliverable).
    emailUndeliverable?: { reason: 'bounce', on: string } | null;
    // The last time they signed in, from the sign-in itself or (for older ones) the audit trail. Absent when they never
    // have: perhaps the email with their first password didn't reach them.
    lastSignInOn?: string;
}

export interface NewUserBindingModel {
    username: string;
    emailAddress: string;
    displayName: string;
    claims: string[];
    timezone: string;
    language: string;
}

// The API replaces every one of these fields, so send the current values of those not being changed.
export interface UpdateUserBindingModel {
    displayName: string;
    timezone: string;
    language: string;
    defaultProjectId?: string;
    status: AccountState;
    claims: string[];
}

export interface ProfileBindingModel {
    displayName: string;
    timezone: string;
    language: string;
    defaultProjectId?: string;
}

export interface LicenseActivationViewModel {
    id: string;
    machineName: string;
    os: string;
    productVersion: string;
    // It runs a CI or development build (-dev, -dev.N), as it last said: it holds no seat.
    devBuild: boolean;
    activatedOn: string;
    lastSeen: string;
}

export interface LicenseViewModel {
    id: string;
    key: string;
    productId: string;
    productName: string;
    productSku: string;
    licensee: string;
    edition: string;
    ownerUsername: string;
    revoked: boolean;
    kind: LicenseKind;
    seats: number;
    // The computers holding a seat: those running a CI or development build don't.
    seatsInUse: number;
    activationsPerYear: number;
    activationsThisYear: number;
    // Each time an administrator reset activationsThisYear, newest first.
    activationResets: ActivationReset[];
    activations: LicenseActivationViewModel[];
    createdOn: string;
    // A review license's: when it ends (unless kept), the press inquiry it came from, whether it's been kept for good,
    // and when the daily job revoked it for having ended.
    reviewExpiresOn?: string;
    reviewTicketId?: string;
    reviewKept?: boolean;
    reviewEndedOn?: string;
}

export interface ActivationReset {
    on: string;
    by: string;
    // How many activations in the last 12 months it cleared.
    cleared: number;
}

export interface LicenseBindingModel {
    productId: string;
    licensee: string;
    edition: string;
    ownerUsername: string;
    revoked: boolean;
}

// standard: sold, or issued by hand (2 seats, 5 activations a year). earlyAccess: an Early Access User's, which only
// activates Early Access Versions (5 seats, 25 a year). review: a reviewer's, issued from a press inquiry (2 seats, 2
// activations), for 30 days unless extended or kept; never created by hand. Set when the license is created, and never
// changed.
export type LicenseKind = 'standard' | 'earlyAccess' | 'review';

export interface NewLicenseBindingModel extends LicenseBindingModel {
    key?: string;
    // Standard when left out. earlyAccess needs an owner with EarlyAccessUserClaim.
    kind?: LicenseKind;
}

export const Editions = ['Standard', 'Educational'];

// The edition sold online (the API's OrderDataProvider.SELLABLE_EDITIONS). Educational licenses are issued by hand.
export const SellableEdition = 'Standard';
export const LICENSEE_MAX_LENGTH = 100;

export type PaymentProvider = 'stripe' | 'paypal' | 'paddle';

// pending: waiting for payment, or for it to clear; fulfilled: paid, license issued; refunded: in full, license revoked;
// cancelled: the checkout expired; failed: the payment was declined.
export type OrderStatus = 'pending' | 'fulfilled' | 'refunded' | 'cancelled' | 'failed';

// The site's switches (Administration > Settings). The API's record has more, which the site doesn't use.
export interface SiteConfig {
    maintenanceMode: boolean;
    ecommerceEnabled: boolean;
    closedMessage?: string;
    // The product the home page features, by SKU (Administration > Settings). Unset (null or undefined), blank, or not
    // published: the first published product by name.
    featuredSku?: string | null;
    // The press page's About Tristar (HTML from RichTextEditor). Unset or blank: the site's own (aboutPressHtml).
    pressAbout?: string;
}

export interface SiteConfigSwitches {
    maintenanceMode?: boolean;
    ecommerceEnabled?: boolean;
    // HTML from RichTextEditor; '' for no notice.
    closedMessage?: string;
    // A product's SKU; '' for the first published by name.
    featuredSku?: string;
    // HTML from RichTextEditor; '' for the site's own wording.
    pressAbout?: string;
}

// Whether the shop is open (its master switch, Administration > Shop), and which payment providers the API has set up,
// open or not. PayPal needs Stripe too (Stripe Tax works out its tax). The site sells only while it's open.
export interface CheckoutConfig {
    enabled: boolean;
    stripe: boolean;
    paypal: boolean;
    paddle: boolean;
    // What the home page says while the shop is closed (Administration > Settings), as HTML: '' for no notice. Undefined
    // until an administrator sets it, when the site's own wording (home.closed) is shown.
    closedMessage?: string;
    // While Paddle is set up: what /checkout/pay opens Paddle's checkout with (the client-side token is public).
    paddleCheckout?: { clientToken: string, environment: 'sandbox' | 'production' };
}

// The providers customers can pay with (as the API's OrderDataProvider.Offers): while Paddle is set up, only Paddle (the
// merchant of record, which takes cards and PayPal on its own checkout); otherwise Stripe and PayPal, as set up.
export function offeredProviders(config: CheckoutConfig | undefined): PaymentProvider[] {
    if (!config) {
        return [];
    }
    return config.paddle ? ['paddle'] : (['stripe', 'paypal'] as PaymentProvider[]).filter(x => config[x]);
}

export interface CheckoutRequest {
    sku: string;
    provider: PaymentProvider;
    // Who the license is for (shown in the product). The account's display name when left out; a guest must give it.
    licensee?: string;
    // A guest's (signed out): where the license and receipt go, and whose account gets it.
    email?: string;
    // PayPal only, for the tax: a two-letter country code.
    country?: string;
    postalCode?: string;
}

// Amounts in the currency's minor units, as ProductPrice.
export interface CheckoutQuote {
    currency: string;
    subtotal: number;
    tax: number;
    total: number;
}

export interface StartedCheckout {
    orderId: string;
    // Stripe's or PayPal's page, where the customer pays.
    redirectUrl: string;
}

export interface OrderViewModel extends CheckoutQuote {
    id: string;
    ownerUsername: string;
    licensee: string;
    productId: string;
    productSku: string;
    productName: string;
    edition: string;
    provider: PaymentProvider;
    status: OrderStatus;
    licenseId?: string;
    // Only for one order, once fulfilled, while the license is valid.
    licenseKey?: string;
    // A guest's order, viewed with its key, once paid: the account the license went to, and whether it was made for it.
    accountEmail?: string;
    accountCreated?: boolean;
    createdOn: string;
    fulfilledOn?: string;
    refundedOn?: string;
}

// amount is in the currency's minor units (cents for USD).
export interface ProductPrice {
    edition: string;
    amount: number;
    currency: string;
}

export interface ProductBindingModel {
    name: string;
    description: string;
    sku: string;
    // The first group of the product's license keys, e.g. ASTREL.
    keyPrefix: string;
    prices: ProductPrice[];
    published: boolean;
    // In Early Access: only invited Early Access Users have licenses, and it isn't on sale yet.
    beta: boolean;
    // The color of the product's parts of the public pages (#rrggbb), usually its user interface's; empty or left out
    // (an API from before there were accent colors): the site's own (components/accent.ts).
    accentColor?: string;
    // For its page's System requirements; left out for a product without any (or an API from before there were any).
    // Sent as null, they're taken away.
    requirements?: ProductRequirements | null;
}

// What a product runs on and needs (tristar-corp-api's ProductRequirements, which these lists copy). Its own list of
// platforms, not OperatingSystems: that one is for downloads, and a mobile app comes from a store.
export const Platforms = ['windows', 'macos', 'linux', 'ios', 'ipados', 'android'] as const;
export type Platform = typeof Platforms[number];
export const MobilePlatforms: readonly Platform[] = ['ios', 'ipados', 'android'];

export const PluginFormats = ['vst3', 'au', 'aax', 'clap', 'lv2', 'auv3', 'standalone'] as const;
export type PluginFormat = typeof PluginFormats[number];

// x64: Intel and AMD. arm64: Apple silicon and other ARM processors.
export const Architectures = ['x64', 'arm64'] as const;
export type Architecture = typeof Architectures[number];

// The formats and architectures a platform can have at all (the API refuses others).
export const PlatformFormats: Record<Platform, readonly PluginFormat[]> = {
    windows: ['vst3', 'aax', 'clap', 'lv2', 'standalone'],
    macos: ['vst3', 'au', 'auv3', 'aax', 'clap', 'lv2', 'standalone'],
    linux: ['vst3', 'clap', 'lv2', 'standalone'],
    ios: ['auv3', 'standalone'],
    ipados: ['auv3', 'standalone'],
    android: ['standalone']
};

export const PlatformArchitectures: Record<Platform, readonly Architecture[]> = {
    windows: ['x64', 'arm64'],
    macos: ['x64', 'arm64'],
    linux: ['x64', 'arm64'],
    ios: ['arm64'],
    ipados: ['arm64'],
    android: ['arm64', 'x64']
};

export const REQUIREMENT_TEXT_MAX_LENGTH = 200;

export interface PlatformRequirements {
    platform: Platform;
    // "Windows 10 (64-bit)", "macOS 11 Big Sur", "iPadOS 16"; may be empty.
    minimumVersion: string;
    architectures: Architecture[];
    formats: PluginFormat[];
    // Mainly mobile: "iPad with an A12 chip or later"; may be empty.
    devices: string;
    // An https App Store or Google Play link; empty for a download.
    storeUrl: string;
}

// Every text may be empty.
export interface ProductRequirements {
    platforms: PlatformRequirements[];
    processor: string;
    memory: string;
    disk: string;
    other: string;
    // What isn't available that can't be worked out from the platforms.
    notAvailable: string;
}

export const OperatingSystems = ['windows', 'macos', 'linux', 'any'] as const;

export type OperatingSystem = typeof OperatingSystems[number];

// Their names are common.operatingSystems (src/i18n).

export interface ProductFileViewModel {
    id: string;
    title: string;
    operatingSystem: OperatingSystem;
    fileName: string;
    fileSize?: number;
    // Lower-case hex, when the administrator entered one.
    sha256?: string;
    // False until the upload has been confirmed; such files are only shown to administrators.
    uploaded: boolean;
    // The website's download page for this file (license holders only). Unlike a pre-signed URL, it doesn't expire.
    downloadUrl: string;
}

export interface ProductVersionViewModel {
    id: string;
    version: string;
    releaseNotes: string;
    isCurrent: boolean;
    // An Early Access Version: left out of the public views (see ProductsApi.earlyAccessVersions). isCurrent then means
    // the current Early Access Version: released versions and Early Access Versions each have their own.
    earlyAccess: boolean;
    // Withdrawn: left out of the public views, and only administrators can download its files. Builds of it still
    // activate and are offered updates. Never current.
    delisted: boolean;
    // By operating system, then title. The public view only has uploaded files, and versions with at least one.
    files: ProductFileViewModel[];
    releasedOn: string;
}

export interface ProductFileBindingModel {
    title: string;
    operatingSystem: OperatingSystem;
    // Optional; empty clears it.
    sha256?: string;
}

export interface ProductImageViewModel {
    id: string;
    url: string;
    altText: string;
    sortOrder: number;
    // In pixels, when known: given to the browser, so it keeps the image's place while it loads.
    width?: number;
    height?: number;
}

// An audio demo, played on the product's page. Public reads only include those whose upload has been confirmed.
export interface ProductDemoViewModel {
    id: string;
    url: string;
    title: string;
    description: string;
    contentType: string;
    fileSize?: number;
    uploaded: boolean;
    sortOrder: number;
    // Whether ProductsApi.getDemoAnalysis has its waveform and spectrum (left out by an API from before there were any).
    hasAnalysis?: boolean;
}

// What a demo sounds like, for the player to draw (components/demoAnalysis.ts works it out).
export interface DemoAnalysis {
    // Seconds.
    duration: number;
    // Base64 of one byte (0-255) per slice of the demo: its loudest sample, scaled to the loudest overall.
    peaks: string;
    // Spectrum frames per second, and frequency bands per frame (low to high).
    frameRate: number;
    bands: number;
    // Base64 of frames × bands bytes (0-255), frame after frame.
    spectrum: string;
}

export interface ProductViewModel extends ProductBindingModel {
    id: string;
    // The current released (GA) version.
    currentVersion?: ProductVersionViewModel;
    // The current Early Access Version: only in the administrators' view.
    currentEarlyAccessVersion?: ProductVersionViewModel;
    // Newest first.
    versions: ProductVersionViewModel[];
    images: ProductImageViewModel[];
    // In play order.
    demos: ProductDemoViewModel[];
    // The press kit: in the public views (and the catalog) only when it's on /press.
    press?: ProductPress;
    createdOn: string;
    modifiedOn: string;
}

// A file of a product's press kit, public on the CDN.
export interface ProductPressFile {
    url: string;
    fileName: string;
    fileSize: number;
    uploadedOn: string;
}

// A zip of the product's images, in order, with alt-text.txt, which the API builds.
export interface ProductPressImagePack extends ProductPressFile {
    imageCount: number;
    // False, in the administrators' view, once an image has been added, changed, moved or removed since it was built.
    upToDate: boolean;
}

export interface ProductPress {
    // On /press, when the product is published too.
    published: boolean;
    // HTML from RichTextEditor, sanitized when shown, as the description is.
    summary: string;
    imagePack?: ProductPressImagePack;
    reviewerNotes?: ProductPressFile;
}

export interface ProductVersionBindingModel {
    version: string;
    releaseNotes: string;
    releasedOn: string;
    earlyAccess: boolean;
}

// A pre-signed S3 URL. Uploads PUT the file to url with Content-Type: contentType.
export interface PresignedUrl {
    url: string;
    contentType?: string;
    expiresAt: string;
}

// Support tickets (tristar-corp-api Src/Models/support). Only the ticket's customer and administrators can see one.
export type SupportTicketState = 'pending' | 'in-review' | 'waiting-for-info' | 'resolved' | 'closed';

export const SupportTicketStates: SupportTicketState[] = ['pending', 'in-review', 'waiting-for-info', 'resolved', 'closed'];

// Their names are support.states (src/i18n).

// Resolved and Closed tickets are finished: the customer can't reply to them.
export const isOpenTicketState = (state: SupportTicketState) => state !== 'resolved' && state !== 'closed';

export interface SupportTicketReason {
    id: string;
    state: SupportTicketState;
    // Who can give it: administrators, the ticket's customer, or only the API itself.
    actor: 'admin' | 'customer' | 'system';
    // The kinds of ticket it's for; without, support tickets only.
    categories?: SupportTicketCategory[];
}

// A press inquiry (/press/contact) is a ticket of its own category, with its own reasons.
export type SupportTicketCategory = 'support' | 'press';

const BOTH: SupportTicketCategory[] = ['support', 'press'];
const PRESS: SupportTicketCategory[] = ['press'];

// The API's list (SupportTicketReasons.ts): every state change needs one of these, for the state being changed to.
// Their names are support.reasons (src/i18n).
export const SupportTicketReasons: SupportTicketReason[] = [
    { id: 'opened', state: 'pending', actor: 'system', categories: BOTH },
    { id: 'under-investigation', state: 'in-review', actor: 'admin' },
    { id: 'information-received', state: 'in-review', actor: 'admin' },
    { id: 'reopened', state: 'in-review', actor: 'admin', categories: BOTH },
    { id: 'checking-outlet', state: 'in-review', actor: 'admin', categories: PRESS },
    { id: 'need-more-details', state: 'waiting-for-info', actor: 'admin' },
    { id: 'need-logs-or-screenshots', state: 'waiting-for-info', actor: 'admin' },
    { id: 'need-steps-to-reproduce', state: 'waiting-for-info', actor: 'admin' },
    { id: 'need-outlet-confirmation', state: 'waiting-for-info', actor: 'admin', categories: PRESS },
    { id: 'fixed-in-release', state: 'resolved', actor: 'admin' },
    { id: 'workaround-provided', state: 'resolved', actor: 'admin' },
    { id: 'configuration-issue', state: 'resolved', actor: 'admin' },
    { id: 'question-answered', state: 'resolved', actor: 'admin' },
    { id: 'press-answered', state: 'resolved', actor: 'admin', categories: PRESS },
    { id: 'review-license-issued', state: 'resolved', actor: 'system', categories: PRESS },
    { id: 'closed-by-customer', state: 'closed', actor: 'customer', categories: BOTH },
    { id: 'no-response', state: 'closed', actor: 'admin', categories: BOTH },
    { id: 'duplicate', state: 'closed', actor: 'admin', categories: BOTH },
    { id: 'not-a-fit', state: 'closed', actor: 'admin', categories: PRESS },
    { id: 'not-a-defect', state: 'closed', actor: 'admin' },
    { id: 'out-of-scope', state: 'closed', actor: 'admin' },
    { id: 'license-revoked', state: 'closed', actor: 'system' },
    { id: 'license-transferred', state: 'closed', actor: 'system' }
];

export const CustomerCloseReason = 'closed-by-customer';

// Whether the reason can be given for a ticket of the category, as the API's SupportTicketReasons.IsFor.
export const isReasonFor = (reason: SupportTicketReason, category: SupportTicketCategory = 'support') =>
    (reason.categories ?? ['support']).includes(category);

// A press inquiry's details. emailMatchesOutlet and reviewLicenseId are only in administrators' views.
export interface SupportTicketPress {
    // The outlet or channel.
    publication: string;
    reviewLicense: boolean;
    // With reviewLicense.
    outletUrl?: string;
    role?: string;
    // Whether the requester's email address is at the outlet's own domain (free mail and platforms never are).
    emailMatchesOutlet?: boolean;
    reviewLicenseId?: string;
}

// The API's limits for a press inquiry (SupportPress.ts).
export const PRESS_PUBLICATION_MAX_LENGTH = 120;
export const PRESS_ROLE_MAX_LENGTH = 100;
export const PRESS_URL_MAX_LENGTH = 500;

// Same limits as the API.
export const SUPPORT_SUBJECT_MAX_LENGTH = 120;
export const SUPPORT_MESSAGE_MAX_LENGTH = 10000;
// A signed-out visitor's name, as the API's SUPPORT_NAME_MAX_LENGTH.
export const SUPPORT_NAME_MAX_LENGTH = 100;

export interface SupportAttachmentViewModel {
    id: string;
    fileName: string;
    contentType: string;
    size: number;
}

export interface SupportTicketMessageViewModel {
    id: string;
    authorUsername: string;
    // Written by an administrator, not the ticket's customer.
    fromSupport: boolean;
    // Plain text: show it as text, never as HTML.
    body: string;
    createdOn: string;
    attachments: SupportAttachmentViewModel[];
}

export interface SupportTicketStateChange {
    on: string;
    // 'SYSTEM' for the API's own changes (a license revoked or transferred).
    by: string;
    from?: SupportTicketState | null;
    to: SupportTicketState;
    reason: string;
}

export interface SupportTicketSummaryViewModel {
    id: string;
    // A press inquiry's (with its press details) or a support ticket's; tickets from before categories are 'support'.
    category?: SupportTicketCategory;
    press?: SupportTicketPress;
    // Empty for a ticket opened signed out: then customerName and customerEmail say who.
    ownerUsername: string;
    customerName?: string;
    customerEmail?: string;
    licenseId: string;
    productId: string;
    productName: string;
    subject: string;
    state: SupportTicketState;
    messageCount: number;
    createdOn: string;
    lastActivityOn: string;
}

export interface SupportTicketViewModel extends SupportTicketSummaryViewModel {
    // A visitor's press inquiry, for administrators: the account with the address it was sent from, if any.
    existingAccount?: string;
    // Oldest first; the first is the synopsis.
    messages: SupportTicketMessageViewModel[];
    stateHistory: SupportTicketStateChange[];
}

// Product documentation, for signed-in users.
export interface DocumentedProductViewModel {
    productId: string;
    sku: string;
    productName: string;
    modifiedOn?: string;
}

export interface ProductDocumentationViewModel extends DocumentedProductViewModel {
    // HTML from RichTextEditor; '' until something has been written.
    content: string;
    version: number;
    // After saving: the doc-image: references that named no image in the library, which were left out.
    missingImages?: string[];
}

// A PDF of the documentation, marked with the user and downloadId. The URL lasts a few minutes.
export interface DocumentationPdf {
    downloadId: string;
    download: PresignedUrl;
}

// An image in a product's documentation library. src is its public link on the CDN: the content can show it by that
// link (the editor) or as <img src="doc-image:{name}"> (HTML written elsewhere), and saving stores its id instead.
export interface DocumentationImage {
    id: string;
    // Unique within the product: lower case letters and digits, joined by dashes.
    name: string;
    altText: string;
    src: string;
    width: number;
    height: number;
    createdOn: string;
    // Whether the saved content shows it.
    inUse: boolean;
}

// Who a documentation PDF was made for (administrators), from the ID printed on it.
export interface DocumentationDownloadViewModel {
    id: string;
    username: string;
    // Missing if the account has since been deleted.
    displayName?: string;
    emailAddress?: string;
    productId: string;
    productName?: string;
    docVersion: number;
    ip: string;
    createdOn: string;
}

// The audit trail (administrators): one request worth keeping, as the API's AuditEntry has it.
export type AuditCategory = 'admin' | 'auth' | 'account' | 'support' | 'commerce' | 'licensing' | 'webhook' | 'news' | 'system';
export const AuditCategories: AuditCategory[] = ['admin', 'auth', 'account', 'support', 'commerce', 'licensing', 'webhook', 'news', 'system'];
export const AuditCategoryNames: Record<AuditCategory, string> = {
    admin: 'Administration', auth: 'Sign-in', account: 'Account', support: 'Support', commerce: 'Purchases',
    licensing: 'Licensing (Astrel)', webhook: 'Payment webhooks', news: 'Mailing list', system: 'System'
};

// success: 2xx. denied: 401/403. failed: any other 4xx. error: 5xx.
export type AuditOutcome = 'success' | 'denied' | 'failed' | 'error';
export const AuditOutcomes: AuditOutcome[] = ['success', 'denied', 'failed', 'error'];

export interface AuditEntryViewModel {
    id: string;
    occurredOn: string;
    requestId: string;
    category: AuditCategory;
    // 'PUT /v1/license/{licenseId}', a websocket action or a direct invocation's event.
    action: string;
    pathParameters: { [name: string]: string };
    statusCode: number;
    outcome: AuditOutcome;
    actor: {
        username: string;
        isAdmin: boolean;
        claims: string[];
        deviceId: string;
        // Sign-ins: the account it was for, whether or not it worked.
        attemptedUsername: string;
    };
    ip: string;
    // What X-Forwarded-For claimed, when it differs from ip.
    forwardedFor: string;
    userAgent: string;
    origin: string;
    // Redacted: ids, numbers, booleans and fixed choices; anything written is replaced with its length.
    body?: unknown;
    details: { [name: string]: string | number | boolean | null };
    // The alert rules it set off.
    alerts: string[];
}

export interface AuditFilter {
    username?: string;
    ip?: string;
    category?: AuditCategory;
    outcome?: AuditOutcome;
    alerted?: boolean;
    // ISO times.
    from?: string;
    to?: string;
}

export interface AuditPage {
    entries: AuditEntryViewModel[];
    // There's more: the next page's before.
    nextBefore?: string;
}

// The site's anonymous visit counts (tristar-corp-api Models/stats/SiteStat.ts): what the site reports as people browse.
export type SiteStatEvent = 'page-view' | 'product-view' | 'checkout-view' | 'checkout-cancelled' | 'entry';

export interface SiteStatHit {
    event: SiteStatEvent;
    // The page's route pattern (siteStats.ts statsPage): page-view and entry.
    path?: string;
    // product-view, checkout-view and checkout-cancelled.
    sku?: string;
    // entry: the referring page, without its query, and the landing address's campaign tags.
    referrer?: string;
    utm?: { source?: string, medium?: string, campaign?: string };
}

export interface SiteStatCount {
    name: string;
    count: number;
}

// One product's funnel over the report's days: views from the visit counts, the rest from the orders made on those days,
// by what became of them. abandoned: expired, or still unpaid after a day.
export interface ProductFunnel {
    sku: string;
    name: string;
    productViews: number;
    checkoutViews: number;
    checkoutCancelled: number;
    // Confirmed sign-ups to hear when it's released (the mailing list).
    releaseSignups: number;
    // Download links given out (counted by the API, never administrators'), to people signed in and signed out (trials
    // and guests). Missing from an API from before.
    downloads?: number;
    guestDownloads?: number;
    checkoutsStarted: number;
    purchased: number;
    refunded: number;
    declined: number;
    abandoned: number;
    inProgress: number;
}

export interface SiteStatsReport {
    from: string;
    to: string;
    daily: { day: string, pageViews: number, visits: number, productViews: number, checkoutViews: number }[];
    countries: { name: string, visits: number, pageViews: number }[];
    languages: { name: string, count: number, countries: SiteStatCount[] }[];
    sources: SiteStatCount[];
    pages: SiteStatCount[];
    landingPages: SiteStatCount[];
    products: ProductFunnel[];
    // Hits from data centers' addresses (automated browsers, nearly all): in none of the counts above. Missing from an
    // API from before they were counted.
    datacenter?: DatacenterStatsReport;
}

// A named day in the visit counts (a campaign's launch, a release): the analytics page compares the days before it
// with the days from it.
export interface SiteStatMarker {
    id: string;
    name: string;
    note: string;
    // YYYY-MM-DD, UTC: the first day it's in effect.
    day: string;
    createdOn: string;
    createdBy: string;
}

export type SiteStatMarkerInput = Pick<SiteStatMarker, 'name' | 'note' | 'day'>;

export interface DatacenterStatsReport {
    daily: { day: string, pageViews: number, visits: number, productViews: number }[];
    countries: SiteStatCount[];
    sources: SiteStatCount[];
    pages: SiteStatCount[];
    landingPages: SiteStatCount[];
    // Product views by product name.
    products: SiteStatCount[];
    // Downloads by product name, signed in or not. Missing from an API from before they were counted.
    downloads?: SiteStatCount[];
}

// The mailing list (NewsApi): people who asked to hear when a product is released, and perhaps for other news.
export type NewsSignupSource = 'product-page' | 'home';

export interface NewsSubscriberView {
    id: string;
    email: string;
    releaseSkus: string[];
    news: boolean;
    confirmedOn: string;
    source: NewsSignupSource;
}

// An unsubscribe link's subscription, for its page.
export interface NewsSubscription {
    email: string;
    products: { sku: string, name: string }[];
    news: boolean;
}

// Who a list email goes to: everyone waiting for a product's release, or everyone who asked for news.
export type NewsAudience = `release:${string}` | 'news';
export type NewsCampaignStatus = 'draft' | 'sending' | 'sent';

export interface NewsCampaign {
    id: string;
    subject: string;
    // The rich-text editor's HTML.
    html: string;
    audience: NewsAudience;
    status: NewsCampaignStatus;
    // What it says now has been sent as a test: needed to send it.
    tested: boolean;
    testedOn?: string;
    sent: number;
    failed: number;
    sendStartedOn?: string;
    sentOn?: string;
    createdOn?: string;
    createdBy?: string;
    // In the list (NewsApi.campaigns): how many it would go to now.
    audienceSize?: number;
}

export interface NewsCampaignBindingModel {
    subject: string;
    html: string;
    audience: NewsAudience;
}

// An administrator's note on a customer's account; the customer never sees it.
export interface UserNoteViewModel {
    id: string;
    text: string;
    createdOn: string;
    createdBy: string;
}
